import { regexEscape } from '../escape-utils'

import { Tool } from './base'
import {
  InlineGroups,
  makeRenderer,
  normalizeList,
  RenderContext,
  RenderOut,
  scalarToString,
} from './render/render-kit'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import { joinDirect, wrapRendererWithDatasource } from './render/rtd/datasource.utils'
import { trendMicroMap } from './render/rtd/tool-datasource-mappings/trendmicro-visionone.datasource'
import { AstPredicate } from './render/types'

export { trendMicroMap }

export class TrendMicroVisionOneTool extends Tool {
  public readonly toolName = 'Trend Micro Vision One'
  public readonly toolKind = 'edr' as const

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    //https://portal.xdr.trendmicro.com
    //Set time to Last 30 days
    const currentDate = new Date()
    const myPastDate = new Date(currentDate)
    myPastDate.setDate(myPastDate.getDate() - 30)
    return `${rootURL}#/app/search?start=${myPastDate.valueOf()}&end=${currentDate.valueOf()}&search_source=telemetry&search_query=${encodedRenderedContent}`
  }

  public pivotEscape(key: string, value: string): string {
    return regexEscape(value)
  }

  /** Trend Micro Vision One XDR — Renderer
   *
   * Fields
   * - Map via toolTuningFields["trendmicro"]; no Eta wrapping.
   *
   * Values & Lists
   * - Strings are **double-quoted** with escaping.
   * - Numbers are **bare** (no quotes) for equality and comparisons.
   * - Multi-values: use `IN ("v1","v2",...)` where applicable.
   *
   * Logic & Grouping
   * - InlineGroups: **no root parentheses**; nested groups parenthesized.
   * - Logical glue: ` AND ` / ` OR `.
   *
   * Operators (default, wildcard style for string ops)
   * - eq / neq:           `F = "v"` / `NOT F = "v"`; lists: `F IN (...)` / `NOT (F IN (...))`
   * - contains:           `F = "*v*"`; list → `F IN (...)`
   * - doesnotcontain:     `NOT F = "*v*"`; list → `NOT (F IN (...))`
   * - startswith/endswith `F = "v*"` / `F = "*v"`; lists use IN/NOT IN pattern
   * - Comparisons:        numeric-safe: `F < 123`, `F > 123` or quoted for strings
   *
   * Null / Empty (best effort; adjust if Vision One expects different built-ins)
   * - isnull     → `F = null`
   * - isnotnull  → `F != null`
   * - isempty    → `F = ""`
   * - isnotempty → `F != ""`
   *
   * Safety
   * - Sanitize identifiers via mapField; centralized quoting/list helpers.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      wrapField: (f) => f, // no Eta for Vision One
      andGlue: ' AND ',
      orGlue: ' OR ',
      // quote(): default double-quote with escaping from render-kit is fine
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))

      // Raw values (to preserve number-vs-string for smart quoting)
      const rawVals =
        p.value == null
          ? [undefined]
          : Array.isArray(p.value)
            ? p.value
            : [p.value]

      // Canonical string forms + multi detection
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Shape wildcards per operator (string ops only)
      const shaped = vals.map((s) => {
        switch (p.op) {
          case 'contains':
            return `*${s}*`
          case 'doesnotcontain':
            return `*${s}*`
          case 'startswith':
            return `${s}*`
          case 'endswith':
            return `*${s}`
          default:
            return s
        }
      })

      // Smart quote: numbers → bare, strings → quoted
      const Q = (s: string, raw: any) =>
        typeof raw === 'number' ? String(raw) : renderContext.quote(s)

      // Build a list matching shaped + raw typing
      const LIST = () =>
        `(${shaped.map((s, i) => Q(s, rawVals[i])).join(', ')})`

      /* -------- Null / Empty -------- */
      switch (p.op) {
        case 'isnull':
          return `NOT ${F}:*`
        case 'isnotnull':
          return `${F}:*`
        case 'isempty':
          return `${F}:""`
        case 'isnotempty':
          return `NOT ${F}:""`
      }

      /* -------- Main operators (single switch) -------- */
      switch (p.op) {
        // Equality / inequality
        case 'eq':
          return isMulti
            ? `${F} IN ${LIST()}`
            : `${F}:${Q(shaped[0], rawVals[0])}`
        case 'neq':
          return isMulti
            ? `NOT (${F} IN ${LIST()})`
            : `NOT ${F}:${Q(shaped[0], rawVals[0])}`

        // String wildcard family via equality + wildcarded values
        case 'contains':
          return isMulti
            ? `${F} IN ${LIST()}`
            : `${F}:${Q(shaped[0], rawVals[0])}`
        case 'doesnotcontain':
          return isMulti
            ? `NOT (${F} IN ${LIST()})`
            : `NOT ${F}:${Q(shaped[0], rawVals[0])}`
        case 'startswith':
          return isMulti
            ? `${F} IN ${LIST()}`
            : `${F}:${Q(shaped[0], rawVals[0])}`
        case 'endswith':
          return isMulti
            ? `${F} IN ${LIST()}`
            : `${F}:${Q(shaped[0], rawVals[0])}`

        // Comparisons (smart quoting keeps numbers bare)
        case 'lt':
          return `${F} < ${Q(shaped[0], rawVals[0])}`
        case 'lte':
          return `${F} <= ${Q(shaped[0], rawVals[0])}`
        case 'gt':
          return `${F} > ${Q(shaped[0], rawVals[0])}`
        case 'gte':
          return `${F} >= ${Q(shaped[0], rawVals[0])}`

        default:
          // Safe default
          return isMulti
            ? `${F} IN ${LIST()}`
            : `${F}:${Q(shaped[0], rawVals[0])}`
      }
    },
    // Fix to always prefix the snippet with AND
    finalize: (out: RenderOut, renderContext: RenderContext): string => {
      const base =
        out.kind === 'text' ? out.text : out.lines.join(renderContext.andGlue)

      if (!base) return ''
      return `${renderContext.andGlue}(${base})`
    },
  })
  public readonly tuningText =
    'Append tuning logic to the end of the hunt logic. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired. Please note any partial string matches will require ** instead of *.'

  public override readonly datasourceMap = trendMicroMap

  /**
   * Emit the Vision One datasource prelude.
   * Format: eventId:X AND eventSubId:Y  (single code)
   *      or eventId:X AND eventSubId:(Y OR Z)  (multiple codes)
   * eventId comes from extra.eventId; subIds from eventCodes[].
   */
  public emitTrendMicroDatasource(ds: NormalizedDataSource | undefined): string {
    if (!ds) return ''
    const eventId = (ds.extra as Record<string, unknown> | undefined)?.['eventId']
    if (!eventId) return ''
    const parts: string[] = [`eventId:${eventId}`]
    const codes = ds.eventCodes ?? []
    if (codes.length === 1) {
      parts.push(`eventSubId:${codes[0]}`)
    } else if (codes.length > 1) {
      parts.push(`eventSubId:(${codes.join(' OR ')})`)
    }
    return parts.join(' AND ')
  }

  /** Emit prelude + finalize()-prefixed tuning body joined directly. */
  public readonly renderTrendMicroVisionOne = wrapRendererWithDatasource(
    this.renderTuning,
    this.toolName,
    (ds) => this.emitTrendMicroDatasource(ds),
    joinDirect,
    this.tuningText,
  )
}
